import clsx from 'clsx'
import dayjs, { Dayjs } from 'dayjs'
import { nothing, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { html } from 'lit/static-html.js'

import {
  CommonFormElementSize,
  FloatingStrategy,
  PopupPlacement,
  TimePreset,
  TimeSelection,
} from '@/common/types'
import { getValidityStateObject } from '@/common/utils'
import { GenericFormElement } from '@/internal/mixin/genericFormElement'
import { i18next, t } from '@/localization/localization'

import DapDSTimeGrid from './time-grid.component'
import styles from './timepicker.scss?inline'

/**
 * `dap-ds-timepicker`
 * @summary A timepicker is a graphical user interface widget that allows the user to select a time from a time range.
 * @element dap-ds-timepicker
 * @title - Timepicker
 *
 * @property {Dayjs | string} value - The value of the timepicker. Can be a Dayjs object or a string in 'HH:mm' format.
 * @property {string} label - The label of the timepicker.
 * @property {string} description - The description of the timepicker.
 * @property {'xs' | 'sm' | 'lg'} size - The size of the timepicker.
 * @property {boolean} disabled - The disabled state of the timepicker.
 * @property {boolean} required - The required state of the timepicker.
 * @property {boolean} readonly - The readonly state of the timepicker.
 * @property {boolean} autofocus - The autofocus state of the timepicker.
 * @property {string} tooltip - The tooltip of the timepicker.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the timepicker.
 * @property {string} feedback - The feedback of the timepicker.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the timepicker.
 * @property {boolean} optional - The optional state of the timepicker.
 * @property {string} optionalLabel - The optional label of the timepicker.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 * @property {string} autocomplete - The autocomplete of the timepicker.
 * @property {string} placeholder - The placeholder of the timepicker.
 * @property {boolean} loading - The loading state of the timepicker.
 * @property {string} format - The format of the timepicker. Defaults to 'HH:mm'.
 * @property {string} minTime - The minimum time of the timepicker. Format: 'HH:mm'.
 * @property {string} maxTime - The maximum time of the timepicker. Format: 'HH:mm'.
 * @property {number} step - The step interval in minutes. Defaults to 15.
 * @property {boolean} openTimeOnInput - Whether the time picker should open on typing.
 * @property {boolean} sendEmptyEventOnInput - Whether the time picker should send an empty event on typing.
 * @property {string} clearButton - Whether the clear button should be shown. Defaults to 'true'.
 * @property {string} clearButtonAriaLabel - The aria label of the clear button.
 * @property {string} placement - The placement of the dropdown of the timepicker.
 * @property {FloatingStrategy} floatingStrategy - The floating strategy of the timepicker.
 * @property {string} locale - The locale of the timepicker.
 * @property {boolean} closeOnSelection - Whether the dropdown should close when a time is selected.
 * @property {boolean} showSeconds - Whether to show the seconds column in the time picker.
 * @property {number} secondsStep - The step interval in seconds. Defaults to 1.
 * @property {function} disabledTime - Callback to determine if a time should be disabled.
 * @property {boolean} hideDisabledTimes - Whether to hide disabled times or show them as disabled.
 * @property {TimePreset[]} presets - Array of preset time slots for quick selection.
 *
 * @event {{ value: Dayjs | string, hour: number, minute: number, second?: number }} dds-change - Fired when the timepicker value changes.
 * @event {{ value: string }} dds-input - Fired when the timepicker input value changes.
 * @event {{ value: Dayjs | string, hour: number, minute: number, second?: number }} dds-valid-time - Fired when the timepicker input value is valid. Happens on manual input typing.
 * @event {{ value: Dayjs | string, hour: number, minute: number, second?: number, type: 'invalid' | 'out-of-range' }} dds-invalid-time - Fired when the timepicker input value is invalid. Happens on manual input typing.
 * @event {{ void }} dds-clear - Fired when the timepicker is cleared.
 * @event {{ void }} dds-focus - Emitted when the timepicker gains focus.
 * @event {{ void }} dds-blur - Emitted when the timepicker loses focus.
 *
 * @csspart base - The main timepicker container.
 * @csspart trigger - The trigger button of the timepicker.
 * @csspart label - The label of the timepicker.
 * @csspart description - The description of the timepicker.
 * @csspart feedback - The feedback of the timepicker.
 * @csspart tooltip - The tooltip of the timepicker.
 * @csspart input - The input of the timepicker.
 * @csspart clear-button - The clear button of the timepicker.
 *
 * @cssproperty --dds-timepicker-background - The background color of the timepicker. (default: var(--dds-fields-background-default))
 * @cssproperty --dds-timepicker-border-color - The border color of the timepicker. (default: var(--dds-border-neutral-base))
 * @cssproperty --dds-timepicker-border-width - The border width of the timepicker. (default: var(--dds-border-width-base, 1px))
 * @cssproperty --dds-timepicker-border-radius - The border radius of the timepicker. (default: var(--dds-radius-base))
 * @cssproperty --dds-timepicker-text-color - The text color of the timepicker. (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-timepicker-placeholder-color - The placeholder text color. (default: var(--dds-text-neutral-subtle))
 * @cssproperty --dds-timepicker-disabled-background - The background color when disabled. (default: var(--dds-background-neutral-stronger))
 * @cssproperty --dds-timepicker-disabled-text - The text color when disabled. (default: var(--dds-text-neutral-disabled))
 * @cssproperty --dds-timepicker-error-border - The border color for error state. (default: var(--dds-border-negative-base))
 * @cssproperty --dds-timepicker-success-border - The border color for success state. (default: var(--dds-border-positive-base))
 * @cssproperty --dds-timepicker-icon-color - The color of the icons. (default: var(--dds-text-icon-neutral-subtle))
 * @cssproperty --dds-timepicker-clear-icon-color - The color of the clear icon. (default: var(--dds-button-subtle-icon-neutral-enabled))
 * @cssproperty --dds-timepicker-popup-background - The background color of the popup. (default: var(--dds-background-neutral-subtle))
 * @cssproperty --dds-timepicker-popup-shadow - The box shadow of the popup. (default: 0 4px 6px -1px rgb(0 0 0 / 8%), 0 2px 4px -1px rgb(0 0 0 / 6%))
 *
 * @cssproperty --dds-timepicker-padding-xs - The padding for extra small size. (default: var(--dds-spacing-200))
 * @cssproperty --dds-timepicker-padding-sm - The padding for small size. (default: var(--dds-spacing-200))
 * @cssproperty --dds-timepicker-padding-lg - The padding for large size. (default: var(--dds-spacing-300))
 * @cssproperty --dds-timepicker-padding-horizontal - The horizontal padding. (default: var(--dds-spacing-300))
 * @cssproperty --dds-timepicker-padding-vertical - The vertical padding. (default: var(--dds-spacing-200))
 * @cssproperty --dds-timepicker-gap - The gap between elements. (default: var(--dds-spacing-100))
 * @cssproperty --dds-timepicker-icon-gap - The gap between icons. (default: var(--dds-spacing-200))
 * @cssproperty --dds-timepicker-action-gap - The gap between action elements. (default: var(--dds-spacing-200))
 * @cssproperty --dds-timepicker-action-padding - The padding for action elements. (default: var(--dds-spacing-300))
 * @cssproperty --dds-timepicker-clear-icon-width - The width of the clear icon. (default: var(--dds-spacing-800))
 * @cssproperty --dds-timepicker-dropdown-icon-right - The right position of the dropdown icon. (default: var(--dds-spacing-600))
 * @cssproperty --dds-timepicker-min-width - The minimum width of the timepicker. (default: 7.5rem)
 *
 */
@customElement('dap-ds-timepicker')
export default class DapDSTimePicker extends GenericFormElement {
  private static nextId = 0

  private readonly inputId: string
  private readonly labelId: string
  private readonly descriptionId: string

  @query('.combobox__input') private readonly triggerInput!: HTMLInputElement
  @query('#time-grid') private readonly timeGrid!: DapDSTimeGrid

  /** The value of the timepicker (selected time).
   * @type {Dayjs | string | Date | number}
   */
  @property({ attribute: true })
  override value?: Dayjs | string | Date | number

  // Internal storage for the Dayjs time value
  private _timeValue?: Dayjs

  // The selected time value (Dayjs object) - internal use only
  private get timeValue(): Dayjs | undefined {
    return this._timeValue
  }

  private set timeValue(value: Dayjs | undefined) {
    this._timeValue = value
  }

  /** The minimum time of the timepicker. This time will be the minimum selectable time.
   * @type {string}
   * @default '00:00'
   */
  @property({ type: String }) minTime = '00:00'

  /** The maximum time of the timepicker. This time will be the maximum selectable time.
   * @type {string}
   * @default '23:59'
   */
  @property({ type: String }) maxTime = '23:59'

  /** The step interval in minutes for the time input.
   * @type {number}
   * @default 5
   */
  @property({ type: Number }) step = 5

  /** The placement of the dropdown of the timepicker.
   * @type {'top-start' | 'top' | 'top-end' | 'right-start' | 'right' | 'right-end' | 'bottom-start' | 'bottom' | 'bottom-end' | 'left-start' | 'left' | 'left-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom-start'

  /** The open state of the timepicker. */
  @property({ type: Boolean, reflect: true }) opened = false

  /** Whether the time picker should open on typing. */
  @property({ type: Boolean }) openTimeOnInput = false

  /** Whether the time picker should send an empty event on typing. */
  @property({ type: Boolean }) sendEmptyEventOnInput = false

  /** The placeholder of the timepicker. */
  @property() placeholder: string | null

  /** The loading state of the timepicker. */
  @property({ type: Boolean }) loading?: boolean = false

  /** The format of the timepicker. This is the time format used for display and validation.
   * @type {string}
   * @default 'HH:mm'
   */
  @property({ type: String }) format = 'HH:mm'

  /** Whether the clear button should be shown. */
  @property({ type: String }) clearButton?: string = 'true'

  /** The aria label of the clear button. */
  @property({ type: String }) clearButtonAriaLabel?: string

  /** The floating strategy of the timepicker. */
  @property({ type: String }) floatingStrategy: FloatingStrategy = 'fixed'

  /** Whether to keep popup open when trigger leaves viewport. */
  @property({ type: Boolean }) disableViewportAutoClose: boolean = false

  /** The locale of the timepicker. By default it uses the global locale from dayjs.
   * @type {'hu' | 'en' | 'de'}
   */
  @property({ type: String }) locale: string = dayjs.locale()

  /** Whether the dropdown should close when a time is selected. */
  @property({ type: Boolean }) closeOnSelection = false

  /** Whether to show the seconds column in the time picker. */
  @property({ type: Boolean }) showSeconds = false

  /** The step interval in seconds for the time input.
   * @type {number}
   * @default 1
   */
  @property({ type: Number }) secondsStep = 1

  /** Callback function to determine if a specific time should be disabled.
   * @param {number} hour - The hour to check (0-23)
   * @param {number} minute - The minute to check (0-59)
   * @param {number} second - The second to check (0-59, optional)
   * @returns {boolean} True if the time should be disabled
   */
  @property({ attribute: false }) disabledTime?: (
    hour: number,
    minute: number,
    second?: number,
  ) => boolean

  /** Whether to hide disabled times from the grid or show them as disabled.
   * @type {boolean}
   * @default false
   */
  @property({ type: Boolean }) hideDisabledTimes = false

  /** Array of preset time slots for quick selection.
   * @type {TimePreset[]}
   */
  @property({ type: Array, attribute: false }) presets?: TimePreset[]

  @state() private setValueState: boolean = true

  static readonly styles = unsafeCSS(styles)

  private languageChangeHandler = () => {
    // Update locale when language changes, but only if locale wasn't explicitly set via attribute
    if (!this.hasAttribute('locale')) {
      const newLocale = i18next.language || dayjs.locale()
      if (this.locale !== newLocale) {
        this.locale = newLocale
        dayjs.locale(newLocale)
        this.requestUpdate()
      }
    }
  }

  constructor() {
    super()
    DapDSTimePicker.nextId++
    const uniqueSuffix =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID().slice(0, 8)
        : Date.now().toString(36)
    this.inputId = `input-${DapDSTimePicker.nextId}-${uniqueSuffix}`
    this.labelId = `label-${DapDSTimePicker.nextId}-${uniqueSuffix}`
    this.descriptionId = `description-${DapDSTimePicker.nextId}-${uniqueSuffix}`
    // Initialize the feedbackId from FeedbackMixin
    this.feedbackId = `feedback-${DapDSTimePicker.nextId}-${uniqueSuffix}`
  }

  connectedCallback() {
    super.connectedCallback()
    // Listen for language changes to update the locale
    i18next.on('languageChanged', this.languageChangeHandler)
    // Initialize locale from current i18next language if not explicitly set via attribute
    if (!this.hasAttribute('locale')) {
      const currentLang = i18next.language || dayjs.locale()
      if (this.locale !== currentLang) {
        this.locale = currentLang
        dayjs.locale(currentLang)
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    // Clean up language change listener
    i18next.off('languageChanged', this.languageChangeHandler)
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)

    // Sync internal timeValue with public value
    this.syncTimeValue()

    if (this.timeValue) {
      this.triggerInput.value = this.getFormattedValue()
      this.updateTimeGridSelection()
    }
  }

  // Override attributeChangedCallback to sync internal state when value changes
  attributeChangedCallback(name: string, oldValue: any, newValue: any) {
    super.attributeChangedCallback(name, oldValue, newValue)

    if (name === 'value' && newValue !== oldValue) {
      this.syncTimeValue()
      this.requestUpdate()
    }
  }

  /**
   * Normalizes a time value to a Dayjs object.
   * Handles Dayjs objects, Date objects, strings (HH:mm or HH:mm:ss format, or date strings), and undefined/null.
   */
  private normalizeTime(
    value: Dayjs | string | Date | number | undefined | null,
  ): Dayjs | undefined {
    if (value === undefined || value === null) {
      return undefined
    }

    // If it's already a Dayjs object, return it
    if (dayjs.isDayjs(value)) {
      return value
    }

    // If it's a Date object, convert it to Dayjs
    if (value instanceof Date) {
      return dayjs(value)
    }

    // If it's a number (timestamp), convert it
    if (typeof value === 'number') {
      return dayjs(value)
    }

    // If it's a string, try to parse it
    if (typeof value === 'string') {
      const trimmed = value.trim()

      // First, check if it's a time format (HH:mm or HH:mm:ss)
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/
      if (timeRegex.test(trimmed)) {
        // It's a time string, parse it with a dummy date
        return dayjs(`2000-01-01 ${trimmed}`)
      }

      // Check if it's a numeric timestamp string
      if (/^\d+$/.test(trimmed)) {
        const numValue = Number(trimmed)
        // Check if it's seconds (typically 10 digits, less than 10000000000)
        if (numValue > 0 && numValue < 10000000000) {
          return dayjs(numValue * 1000)
        }
        // Check if it's a valid timestamp (milliseconds since epoch)
        if (numValue >= 0 && numValue <= 4102444800000) {
          return dayjs(numValue)
        }
      }

      // Try to parse it as a date string (dayjs will handle various formats)
      const parsed = dayjs(trimmed)
      if (parsed.isValid()) {
        return parsed
      }

      // If all else fails, return undefined
      return undefined
    }

    return undefined
  }

  // Sync internal _timeValue with public value property
  private syncTimeValue() {
    const normalized = this.normalizeTime(this.value)
    this._timeValue = normalized
    // Update the value to be a Dayjs object if it was normalized from a string/Date/number
    // This ensures the value is always in the correct format
    if (normalized) {
      // Only update if the current value is not already the same Dayjs object
      if (
        !dayjs.isDayjs(this.value) ||
        !normalized.isSame(this.value, 'minute')
      ) {
        this.value = normalized
      }
    } else if (this.value !== undefined && this.value !== null) {
      // Clear the value if normalization failed
      this.value = undefined
    }
  }

  protected updated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.updated(_changedProperties)

    if (_changedProperties.has('value')) {
      const normalized = this.normalizeTime(this.value)
      if (normalized) {
        this._timeValue = normalized
        // Update the internal value to be a Dayjs object
        // Only update if it's different to prevent unnecessary updates
        if (!dayjs.isDayjs(this.value) || !normalized.isSame(this.value)) {
          this.value = normalized
        }
      } else {
        this._timeValue = undefined
        // Only clear if there's actually a value to clear
        if (this.value !== undefined && this.value !== null) {
          this.value = undefined
        }
      }
      if (this.setValueState) {
        this.triggerInput.value = this.getFormattedValue()
        this.updateTimeGridSelection()
      }
      this.setValidity()
    }

    if (_changedProperties.has('step') && this.timeGrid) {
      this.timeGrid.step = this.step
    }

    if (_changedProperties.has('minTime') && this.timeGrid) {
      this.timeGrid.minTime = this.minTime
    }

    if (_changedProperties.has('maxTime') && this.timeGrid) {
      this.timeGrid.maxTime = this.maxTime
    }

    if (_changedProperties.has('locale') && this.timeGrid) {
      this.timeGrid.locale = this.locale
    }

    if (_changedProperties.has('showSeconds') && this.timeGrid) {
      this.timeGrid.showSeconds = this.showSeconds
    }

    if (_changedProperties.has('secondsStep') && this.timeGrid) {
      this.timeGrid.secondsStep = this.secondsStep
    }

    if (_changedProperties.has('disabledTime') && this.timeGrid) {
      this.timeGrid.disabledTime = this.disabledTime
    }

    if (_changedProperties.has('hideDisabledTimes') && this.timeGrid) {
      this.timeGrid.hideDisabledTimes = this.hideDisabledTimes
    }

    if (_changedProperties.has('presets') && this.timeGrid) {
      this.timeGrid.presets = this.presets
    }
  }

  private updateTimeGridSelection() {
    if (this.timeValue && this.timeGrid) {
      this.timeGrid.selectedHour = this.timeValue.hour()
      this.timeGrid.selectedMinute = this.timeValue.minute()
      if (this.showSeconds) {
        this.timeGrid.selectedSecond = this.timeValue.second()
      }
    }
  }

  private getHourValue(): number {
    return this.timeValue ? this.timeValue.hour() : 12
  }

  private getMinuteValue(): number {
    return this.timeValue ? this.timeValue.minute() : 0
  }

  private getSecondValue(): number {
    return this.timeValue ? this.timeValue.second() : 0
  }

  getFormattedValue() {
    if (!this.timeValue) {
      return ''
    }

    return dayjs(this.timeValue).format(this.format)
  }

  isValidTime(value: string) {
    if (!value) {
      return false
    }

    // Check if the time format is valid (supports both HH:mm and HH:mm:ss)
    const timeRegex = this.showSeconds
      ? /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/
      : /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/

    if (!timeRegex.test(value)) {
      return false
    }

    // Check if time is within min/max range
    const inputTime = dayjs(`2000-01-01 ${value}`)
    const minTime = dayjs(`2000-01-01 ${this.minTime}`)
    const maxTime = dayjs(`2000-01-01 ${this.maxTime}`)

    const isInRange =
      inputTime.isSame(minTime) ||
      (inputTime.isAfter(minTime) && inputTime.isBefore(maxTime)) ||
      inputTime.isSame(maxTime)

    if (!isInRange) {
      return false
    }

    // Check if time is disabled via callback
    if (this.disabledTime) {
      const hour = inputTime.hour()
      const minute = inputTime.minute()
      const second = this.showSeconds ? inputTime.second() : undefined
      return !this.disabledTime(hour, minute, second)
    }

    return true
  }

  isValueValid() {
    if (!this.timeValue) {
      return true
    }

    return this.isInputValid()
  }

  isInputValid() {
    return this.isValidTime(this.triggerInput.value)
  }

  setValue(
    value?: Dayjs,
    setInputValue: boolean = true,
    sendEmptyEventOnInput: boolean = true,
  ) {
    this._timeValue = value
    this.value = value

    if (setInputValue) {
      this.setValueState = true
      this.triggerInput.value = this.getFormattedValue()
      this.updateTimeGridSelection()
    } else {
      this.setValueState = false
    }

    // Update form value for form participation
    if (this.internals) {
      this.internals.setFormValue(
        this.value
          ? this.showSeconds
            ? this.value.format('HH:mm:ss')
            : this.value.format('HH:mm')
          : this.defaultValue,
      )
    }

    this.setValidity()

    if (this.closeOnSelection) {
      this.hideTimePicker()
    }

    if (sendEmptyEventOnInput) {
      const eventData = {
        value: this.timeValue,
        hour: this.timeValue?.hour(),
        minute: this.timeValue?.minute(),
        ...(this.showSeconds && { second: this.timeValue?.second() }),
      }

      this.emit('dds-change', eventData)
      this.emit('dds-valid-time', {
        value: this.triggerInput.value,
        hour: this.timeValue?.hour(),
        minute: this.timeValue?.minute(),
        ...(this.showSeconds && { second: this.timeValue?.second() }),
      })
    }
  }

  async clearSelection() {
    this.setValue(undefined, true, true)
  }

  private readonly handleDocumentMouseDown = (event: MouseEvent) => {
    const path = event.composedPath()
    if (this && !path.includes(this)) {
      this.hideTimePicker()
    }
  }

  private readonly handlePopupAutoClose = () => {
    this.hideTimePicker({ skipFocus: true })
  }

  private readonly handleInput = (event: InputEvent) => {
    const input = event.target as HTMLInputElement

    if (!input.value) {
      this.setValue(undefined, false, true)
    } else {
      if (this.isInputValid()) {
        const time = dayjs(`2000-01-01 ${input.value}`)
        this.setValue(time, false)
        this.emit('dds-valid-time', {
          value: input.value,
          type: 'valid',
          minute: time.minute(),
          hour: time.hour(),
        })
      } else {
        this.setValue(undefined, false, this.sendEmptyEventOnInput)
        this.emit('dds-invalid-time', {
          value: input.value,
          type: 'invalid',
          hour: input.value.split(':')[0],
          minute: input.value.split(':')[1],
        })
      }
    }

    if (this.openTimeOnInput && !this.opened) {
      this.showTimePicker()
    }

    this.emit('dds-input', { value: input.value })
  }

  private readonly handleKeyDown = async (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      return
    }

    if (
      event.key === 'Enter' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp'
    ) {
      event.preventDefault()
      event.stopPropagation()

      if (!this.opened) {
        this.showTimePicker()
      } else {
        this.hideTimePicker()
      }
    }
  }

  public get focusElement() {
    return this.triggerInput
  }

  private readonly handleClick = () => {
    if (!this.opened) {
      this.showTimePicker()
    } else {
      this.hideTimePicker()
    }
  }

  private readonly handleClearClick = () => {
    if (!this.disabled) {
      this.clearSelection()

      this.triggerInput.focus()
      this.setValidity()

      this.hideTimePicker()

      this.emit('dds-clear')
    }
  }

  private readonly handleTimeChange = (event: CustomEvent<TimeSelection>) => {
    const { hour, minute, second } = event.detail
    const time = dayjs()
      .hour(hour)
      .minute(minute)
      .second(second ?? 0)
      .millisecond(0)
    this.setValue(time, true, true)
  }

  async showTimePicker() {
    if (this.disabled || this.readonly || this.opened) {
      return
    }

    this.opened = true

    document.addEventListener('mousedown', this.handleDocumentMouseDown)
  }

  async hideTimePicker(options?: { skipFocus?: boolean }) {
    if (!this.opened) {
      return
    }

    this.opened = false

    document.removeEventListener('mousedown', this.handleDocumentMouseDown)

    await this.updateComplete
    if (!options?.skipFocus) {
      this.focus()
    }
  }

  private readonly handleFocus = () => {
    this.emit('dds-focus')
  }

  private readonly handleBlur = () => {
    this.emit('dds-blur')
  }

  private readonly handlePopupOpened = () => {
    // When popup opens, scroll to selected time in the grid
    if (this.timeGrid) {
      // Use requestAnimationFrame to ensure the popup is fully visible
      requestAnimationFrame(() => {
        this.timeGrid.scrollToSelectedTime()
      })
    }
  }

  // custom validation
  getValidity() {
    return !!this.value
  }

  get validity(): ValidityState {
    if (this.internals.validity) {
      return this.internals.validity
    } else {
      return getValidityStateObject({
        valueMissing: this.required && !this.getValidity(),
        patternMismatch: !this.isValueValid(),
      })
    }
  }

  checkValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  reportValidity() {
    return this.required && this.getValidity() && this.isValueValid()
  }

  setValidity() {
    if (!this.checkValidity()) {
      this.internals.setValidity(
        {
          valueMissing: this.required && !this.getValidity(),
          patternMismatch: !this.isValueValid(),
        },
        this.validationMessage,
        this.triggerInput,
      )
    } else {
      this.internals.setValidity({})
    }
  }

  handleInvalid(event: Event) {
    event.preventDefault()
  }

  render() {
    return html` <div class="label-container">
        ${this.label &&
        html`<dap-ds-form-label
          part="label"
          id=${this.labelId}
          .size=${this.size}
          .required=${this.required}
          .optional=${this.optional}
          .disabled=${this.disabled}
          class=${clsx('label')}
          .noMargin=${!!this.description}
          for=${this.inputId}
          label=${ifDefined(this.label)}>
        </dap-ds-form-label>`}
        ${this.tooltip &&
        html` <dap-ds-tooltip
          part="tooltip"
          content=${this.tooltip}
          mode="toggle"
          placement=${this.tooltipPlacement}>
          <dap-ds-icon-button
            slot="trigger"
            size=${this.size as 'xs' | 'sm' | 'md' | 'lg'}
            iconSize=${this.size === 'xs' ? '16' : '22'}
            clean>
            <dap-ds-icon
              name="information-line"
              aria-label=${this.tooltipAriaLabel}
              class="info-button"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-tooltip>`}
      </div>
      ${this.description &&
      html`
        <dap-ds-typography
          part="description"
          variant="description"
          class=${clsx('input-description')}
          size=${ifDefined(this.size as CommonFormElementSize)}
          id=${this.descriptionId}>
          ${this.description}
        </dap-ds-typography>
      `}
      <div>
        <dap-ds-popup
          part="base"
          class="select-popup"
          overflow
          ?opened=${this.opened}
          ?hasArrow=${false}
          placement=${this.placement}
          floatingStrategy=${this.floatingStrategy}
          maxHeight="auto"
          offset="4"
          ?disableViewportAutoClose=${this.disableViewportAutoClose}
          @dds-close=${this.handlePopupAutoClose}
          @dds-opened=${this.handlePopupOpened}>
          <div
            slot="trigger"
            part="trigger"
            class="${clsx('select', `select--${this.size}`, {
              'select--disabled': this.disabled,
              'select--readonly': this.readonly,
              'select--opened': this.opened,
              'select--loading': this.loading,
              'select--success': this.status === 'success',
              'select--error': this.status === 'error',
            })}">
            <input
              part="input"
              role="combobox"
              class="${clsx('combobox__input', 'input', `input--${this.size}`, {
                'input--disabled': this.disabled,
                'input--readonly': this.readonly,
              })}"
              id=${this.inputId}
              value=${this.getFormattedValue()}
              aria-expanded=${this.opened}
              aria-haspopup="dialog"
              aria-labelledby=${ifDefined(this.label ? this.labelId : nothing)}
              aria-describedby="${ifDefined(
                this.description ? this.descriptionId : nothing,
              )} ${ifDefined(this.feedback ? this.feedbackId : nothing)}"
              aria-disabled=${this.disabled}
              aria-controls="time-grid"
              aria-autocomplete="none"
              ?required=${this.required}
              ?readonly=${this.readonly}
              ?autofocus=${this.autofocus}
              ?disabled=${this.disabled}
              autocomplete=${this.autocomplete as any}
              placeholder=${ifDefined(this.placeholder ?? '')}
              @blur=${this.handleBlur}
              @focus=${this.handleFocus}
              @input=${this.handleInput}
              @keydown=${this.handleKeyDown}
              @invalid=${this.handleInvalid}
              @click=${this.handleClick} />
            <div
              class="${clsx('select-action', `select-action--${this.size}`, {
                'select-action--disabled': this.disabled,
                'select-action--readonly': this.readonly,
                'select-action--opened': this.opened,
              })}">
              ${this.clearButton === 'true' && !this.readonly
                ? html`<div class="${clsx('clear-icon')}">
                    <dap-ds-icon-button
                      icon="close-line"
                      part="clear-button"
                      aria-label=${ifDefined(
                        this.clearButtonAriaLabel ??
                          t('clear', { lng: this.locale }),
                      )}
                      ?disabled=${this.disabled}
                      @click=${this.handleClearClick}></dap-ds-icon-button>
                  </div>`
                : nothing}
              <div
                class="${clsx('indicator-icon', {
                  'indicator-icon--loading': this.loading,
                })}">
                ${this.loading
                  ? html`<dap-ds-icon-loading-spinner></dap-ds-icon-loading-spinner>`
                  : html`<dap-ds-icon-time-line
                      aria-hidden="true"
                      part="time-button"
                      class=${clsx('time-icon')}
                      @click=${this.handleClick}></dap-ds-icon-time-line>`}
              </div>
            </div>
          </div>
          <dap-ds-time-grid
            id="time-grid"
            part="time-grid"
            .selectedHour=${this.getHourValue()}
            .selectedMinute=${this.getMinuteValue()}
            .selectedSecond=${this.getSecondValue()}
            .minTime=${this.minTime}
            .maxTime=${this.maxTime}
            .step=${this.step}
            .locale=${this.locale}
            .showSeconds=${this.showSeconds}
            .secondsStep=${this.secondsStep}
            .disabledTime=${this.disabledTime}
            .hideDisabledTimes=${this.hideDisabledTimes}
            .presets=${this.presets}
            @dds-change=${(event: CustomEvent<TimeSelection>) => {
              event.stopPropagation()
              event.preventDefault()
              this.handleTimeChange(event)
            }}>
          </dap-ds-time-grid>
        </dap-ds-popup>
        ${this.feedback &&
        html`<dap-ds-feedback
          part="feedback"
          feedbackType=${ifDefined(this.feedbackType)}
          feedbackSize="${ifDefined(this.size as 'lg' | 'sm' | 'xs')}"
          id="feedback">
          ${this.feedback}
        </dap-ds-feedback>`}
      </div>`
  }
}
